import { useState } from "react";
import { DataTable } from "../components/DataTable";

// ── Types ────────────────────────────────────────────────────────────────────

type TierName = "Bronze" | "Silver" | "Gold" | "Platinum";

interface Tier {
  name: TierName;
  minPoints: number;
  discount: number;      // % discount on orders
  earnMultiplier: number; // e.g. 1.5x
  color: string;
  bg: string;
  icon: string;
}

interface LoyaltyMember {
  id: string;
  customerName: string;
  email: string;
  phone: string;
  points: number;
  lifetimePoints: number;
  tier: TierName;
  joinedOn: string;
  lastActivity: string;
  status: "Active" | "Inactive" | "Suspended";
}

interface PointsRule {
  id: string;
  ruleName: string;
  event: string;
  pointsAwarded: number;
  isActive: boolean;
}

interface Redemption {
  id: string;
  customerName: string;
  pointsUsed: number;
  valueRedeemed: number;  // ₹
  orderId: string;
  redeemedOn: string;
  status: "Approved" | "Pending" | "Rejected";
}

// ── Static Data ──────────────────────────────────────────────────────────────

const TIERS: Tier[] = [
  { name: "Bronze",   minPoints: 0,    discount: 2,  earnMultiplier: 1.0, color: "#92400e", bg: "#fef3c7", icon: "bi-award"        },
  { name: "Silver",   minPoints: 1000, discount: 5,  earnMultiplier: 1.5, color: "#475569", bg: "#f1f5f9", icon: "bi-award-fill"   },
  { name: "Gold",     minPoints: 5000, discount: 8,  earnMultiplier: 2.0, color: "#b45309", bg: "#fffbeb", icon: "bi-star-fill"    },
  { name: "Platinum", minPoints: 15000,discount: 12, earnMultiplier: 3.0, color: "#5b21b6", bg: "#ede9fe", icon: "bi-gem"          },
];

const initialMembers: LoyaltyMember[] = [
  { id: "lm1", customerName: "Priya Sharma",  email: "priya@example.com",  phone: "9876543210", points: 4800,  lifetimePoints: 12600, tier: "Silver",  joinedOn: "2025-03-15", lastActivity: "2026-05-18", status: "Active"   },
  { id: "lm2", customerName: "Anjali Mehta",  email: "anjali@example.com", phone: "9123456780", points: 16200, lifetimePoints: 31000, tier: "Platinum",joinedOn: "2024-11-01", lastActivity: "2026-05-20", status: "Active"   },
  { id: "lm3", customerName: "Sunita Rao",    email: "sunita@example.com", phone: "9988776655", points: 320,   lifetimePoints: 1600,  tier: "Bronze",  joinedOn: "2025-08-20", lastActivity: "2026-04-10", status: "Active"   },
  { id: "lm4", customerName: "Meera Joshi",   email: "meera@example.com",  phone: "9001122334", points: 7400,  lifetimePoints: 9800,  tier: "Gold",    joinedOn: "2025-01-10", lastActivity: "2026-05-15", status: "Active"   },
  { id: "lm5", customerName: "Kavya Nair",    email: "kavya@example.com",  phone: "9777888999", points: 0,     lifetimePoints: 400,   tier: "Bronze",  joinedOn: "2025-12-01", lastActivity: "2026-01-14", status: "Inactive" },
  { id: "lm6", customerName: "Divya Singh",   email: "divya@example.com",  phone: "9555444333", points: 2100,  lifetimePoints: 5200,  tier: "Silver",  joinedOn: "2024-09-05", lastActivity: "2026-05-17", status: "Active"   },
  { id: "lm7", customerName: "Ritu Agarwal",  email: "ritu@example.com",   phone: "9222111000", points: 18500, lifetimePoints: 42000, tier: "Platinum",joinedOn: "2024-06-12", lastActivity: "2026-05-19", status: "Active"   },
  { id: "lm8", customerName: "Pooja Verma",   email: "pooja@example.com",  phone: "9333444555", points: 600,   lifetimePoints: 1100,  tier: "Bronze",  joinedOn: "2026-02-08", lastActivity: "2026-03-22", status: "Suspended"},
];

const initialRules: PointsRule[] = [
  { id: "r1", ruleName: "Purchase Reward",      event: "On every order (per ₹100 spent)", pointsAwarded: 10, isActive: true  },
  { id: "r2", ruleName: "Welcome Bonus",        event: "First account registration",       pointsAwarded: 200, isActive: true  },
  { id: "r3", ruleName: "Birthday Bonus",       event: "On customer birthday",             pointsAwarded: 500, isActive: true  },
  { id: "r4", ruleName: "Referral Reward",      event: "When a referred user places order",pointsAwarded: 300, isActive: true  },
  { id: "r5", ruleName: "Review Submission",    event: "Verified product review",          pointsAwarded: 50,  isActive: true  },
  { id: "r6", ruleName: "App Install Bonus",    event: "First login via mobile app",       pointsAwarded: 100, isActive: false },
  { id: "r7", ruleName: "Flash Sale Bonus",     event: "Purchase during flash sale",       pointsAwarded: 150, isActive: true  },
  { id: "r8", ruleName: "Social Share Reward",  event: "Share product on social media",    pointsAwarded: 25,  isActive: false },
];

const initialRedemptions: Redemption[] = [
  { id: "rd1", customerName: "Anjali Mehta",  pointsUsed: 1000, valueRedeemed: 100, orderId: "#ORD-5521", redeemedOn: "2026-05-20", status: "Approved" },
  { id: "rd2", customerName: "Meera Joshi",   pointsUsed: 500,  valueRedeemed: 50,  orderId: "#ORD-5498", redeemedOn: "2026-05-18", status: "Approved" },
  { id: "rd3", customerName: "Priya Sharma",  pointsUsed: 2000, valueRedeemed: 200, orderId: "#ORD-5441", redeemedOn: "2026-05-15", status: "Pending"  },
  { id: "rd4", customerName: "Ritu Agarwal",  pointsUsed: 5000, valueRedeemed: 500, orderId: "#ORD-5390", redeemedOn: "2026-05-12", status: "Approved" },
  { id: "rd5", customerName: "Divya Singh",   pointsUsed: 800,  valueRedeemed: 80,  orderId: "#ORD-5310", redeemedOn: "2026-05-08", status: "Rejected" },
  { id: "rd6", customerName: "Sunita Rao",    pointsUsed: 200,  valueRedeemed: 20,  orderId: "#ORD-5287", redeemedOn: "2026-05-01", status: "Approved" },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const TIER_META: Record<TierName, Tier> = Object.fromEntries(TIERS.map(t => [t.name, t])) as Record<TierName, Tier>;

const STATUS_META: Record<LoyaltyMember["status"], { color: string; bg: string; icon: string }> = {
  Active:    { color: "#16a34a", bg: "#dcfce7", icon: "bi-check-circle-fill" },
  Inactive:  { color: "#64748b", bg: "#f1f5f9", icon: "bi-dash-circle"       },
  Suspended: { color: "#dc2626", bg: "#fee2e2", icon: "bi-slash-circle-fill" },
};

const RD_META: Record<Redemption["status"], { color: string; bg: string }> = {
  Approved: { color: "#16a34a", bg: "#dcfce7" },
  Pending:  { color: "#d97706", bg: "#fef3c7" },
  Rejected: { color: "#dc2626", bg: "#fee2e2" },
};

function fmt(n: number) { return n.toLocaleString("en-IN"); }
function badge(label: string, color: string, bg: string, icon?: string) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg }}>
      {icon && <i className={`bi ${icon}`} style={{ fontSize: 10 }} />}
      {label}
    </span>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

type Tab = "members" | "rules" | "redemptions";

export function LoyaltyPointsPage() {
  const [tab, setTab]                     = useState<Tab>("members");
  const [members, setMembers]             = useState(initialMembers);
  const [rules, setRules]                 = useState(initialRules);
  const [redemptions, setRedemptions]     = useState(initialRedemptions);
  const [search, setSearch]               = useState("");
  const [filterTier, setFilterTier]       = useState<TierName | "All">("All");
  const [filterStatus, setFilterStatus]   = useState<LoyaltyMember["status"] | "All">("All");

  // ── Adjust Points modal state
  const [adjustModal, setAdjustModal]     = useState(false);
  const [selectedMember, setSelectedMember] = useState<LoyaltyMember | null>(null);
  const [adjustPoints, setAdjustPoints]   = useState("");
  const [adjustReason, setAdjustReason]   = useState("");
  const [adjustType, setAdjustType]       = useState<"add" | "deduct">("add");

  // ── Edit Rule modal
  const [ruleModal, setRuleModal]         = useState(false);
  const [editRule, setEditRule]           = useState<PointsRule | null>(null);

  // ── Tiers modal
  const [tiersModal, setTiersModal]       = useState(false);
  const [editTiers, setEditTiers]         = useState<Tier[]>(TIERS.map(t => ({ ...t })));

  // ── Summary stats
  const totalMembers    = members.length;
  const activeMembers   = members.filter(m => m.status === "Active").length;
  const totalPointsOut  = members.reduce((s, m) => s + m.points, 0);
  const platinumCount   = members.filter(m => m.tier === "Platinum").length;

  // ── Filtered members
  const filteredMembers = members.filter(m => {
    const q = search.toLowerCase();
    const matchQ = !q || m.customerName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
    const matchT = filterTier === "All" || m.tier === filterTier;
    const matchS = filterStatus === "All" || m.status === filterStatus;
    return matchQ && matchT && matchS;
  });

  function openAdjust(member: LoyaltyMember) {
    setSelectedMember(member);
    setAdjustPoints("");
    setAdjustReason("");
    setAdjustType("add");
    setAdjustModal(true);
  }

  function applyAdjust() {
    const pts = parseInt(adjustPoints, 10);
    if (!pts || !selectedMember) return;
    setMembers(prev => prev.map(m => {
      if (m.id !== selectedMember.id) return m;
      const delta   = adjustType === "add" ? pts : -pts;
      const newPts  = Math.max(0, m.points + delta);
      const lifetime = adjustType === "add" ? m.lifetimePoints + pts : m.lifetimePoints;
      // Recalculate tier
      const newTier: TierName =
        lifetime >= 15000 ? "Platinum" :
        lifetime >= 5000  ? "Gold"     :
        lifetime >= 1000  ? "Silver"   : "Bronze";
      return { ...m, points: newPts, lifetimePoints: lifetime, tier: newTier };
    }));
    setAdjustModal(false);
  }

  function toggleRule(id: string) {
    setRules(prev => prev.map(r => r.id === id ? { ...r, isActive: !r.isActive } : r));
  }

  function saveRule() {
    if (!editRule) return;
    setRules(prev => prev.map(r => r.id === editRule.id ? editRule : r));
    setRuleModal(false);
  }

  function updateRedemptionStatus(id: string, status: Redemption["status"]) {
    setRedemptions(prev => prev.map(r => r.id === id ? { ...r, status } : r));
  }

  // ── Member table columns
  const memberColumns = [
    {
      key: "customerName", label: "Customer",
      render: (row: LoyaltyMember) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{row.customerName}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>{row.email}</div>
        </div>
      ),
    },
    {
      key: "tier", label: "Tier",
      render: (row: LoyaltyMember) => {
        const t = TIER_META[row.tier];
        return badge(row.tier, t.color, t.bg, t.icon);
      },
    },
    {
      key: "points", label: "Current Points",
      render: (row: LoyaltyMember) => <span style={{ fontWeight: 700, color: "#ec4899" }}>{fmt(row.points)} pts</span>,
    },
    {
      key: "lifetimePoints", label: "Lifetime Points",
      render: (row: LoyaltyMember) => <span style={{ color: "#475569" }}>{fmt(row.lifetimePoints)}</span>,
    },
    { key: "lastActivity", label: "Last Activity", render: (row: LoyaltyMember) => row.lastActivity },
    {
      key: "status", label: "Status",
      render: (row: LoyaltyMember) => {
        const s = STATUS_META[row.status];
        return badge(row.status, s.color, s.bg, s.icon);
      },
    },
    {
      key: "id", label: "Actions",
      render: (row: LoyaltyMember) => (
        <button
          onClick={() => openAdjust(row)}
          style={{ padding: "5px 14px", borderRadius: 8, border: "1px solid #ec4899", background: "#fff", color: "#ec4899", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
        >
          <i className="bi bi-pencil" style={{ marginRight: 4 }} />Adjust Points
        </button>
      ),
    },
  ];

  // ── Redemption table columns
  const redemptionColumns = [
    { key: "orderId",       label: "Order ID",       render: (row: Redemption) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{row.orderId}</span> },
    { key: "customerName",  label: "Customer",       render: (row: Redemption) => row.customerName },
    { key: "pointsUsed",    label: "Points Used",    render: (row: Redemption) => <span style={{ fontWeight: 600, color: "#f59e0b" }}>{fmt(row.pointsUsed)} pts</span> },
    { key: "valueRedeemed", label: "Value",          render: (row: Redemption) => <span style={{ fontWeight: 600 }}>₹{fmt(row.valueRedeemed)}</span> },
    { key: "redeemedOn",    label: "Date",           render: (row: Redemption) => row.redeemedOn },
    {
      key: "status", label: "Status",
      render: (row: Redemption) => {
        const m = RD_META[row.status];
        return badge(row.status, m.color, m.bg);
      },
    },
    {
      key: "id", label: "Actions",
      render: (row: Redemption) => row.status === "Pending" ? (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => updateRedemptionStatus(row.id, "Approved")} style={{ padding: "4px 10px", borderRadius: 7, border: "none", background: "#dcfce7", color: "#16a34a", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Approve</button>
          <button onClick={() => updateRedemptionStatus(row.id, "Rejected")} style={{ padding: "4px 10px", borderRadius: 7, border: "none", background: "#fee2e2", color: "#dc2626", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Reject</button>
        </div>
      ) : <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>,
    },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>

      {/* ── Summary Cards ────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Total Members",    value: fmt(totalMembers),   icon: "bi-people-fill",          color: "#6366f1", bg: "#eef2ff" },
          { label: "Active Members",   value: fmt(activeMembers),  icon: "bi-person-check-fill",    color: "#16a34a", bg: "#dcfce7" },
          { label: "Points in Wallet", value: fmt(totalPointsOut), icon: "bi-coin",                 color: "#f59e0b", bg: "#fef3c7" },
          { label: "Platinum Members", value: fmt(platinumCount),  icon: "bi-gem",                  color: "#7c3aed", bg: "#ede9fe" },
        ].map(c => (
          <div key={c.label} style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 6px rgba(0,0,0,.06)", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className={`bi ${c.icon}`} style={{ fontSize: 20, color: c.color }} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#1e293b" }}>{c.value}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tier Overview Cards ──────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", marginBottom: 24, boxShadow: "0 1px 6px rgba(0,0,0,.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Tier Overview</div>
          <button
            onClick={() => { setEditTiers(TIERS.map(t => ({ ...t }))); setTiersModal(true); }}
            style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
          >
            <i className="bi bi-pencil" style={{ marginRight: 5 }} />Edit Tiers
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {TIERS.map(t => {
            const count = members.filter(m => m.tier === t.name).length;
            return (
              <div key={t.name} style={{ border: `1.5px solid ${t.color}30`, borderRadius: 12, padding: "14px 16px", background: t.bg }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <i className={`bi ${t.icon}`} style={{ color: t.color, fontSize: 18 }} />
                  <span style={{ fontWeight: 700, color: t.color, fontSize: 14 }}>{t.name}</span>
                </div>
                <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.8 }}>
                  <div>Min Points: <b>{fmt(t.minPoints)}</b></div>
                  <div>Discount: <b>{t.discount}%</b></div>
                  <div>Earn Multiplier: <b>{t.earnMultiplier}×</b></div>
                  <div>Members: <b>{count}</b></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,.06)", overflow: "hidden" }}>
        {/* Tab Bar */}
        <div style={{ display: "flex", borderBottom: "2px solid #f1f5f9", padding: "0 20px" }}>
          {([
            { key: "members",     label: "Members",     icon: "bi-people"         },
            { key: "rules",       label: "Earning Rules",icon: "bi-sliders"        },
            { key: "redemptions", label: "Redemptions",  icon: "bi-arrow-left-right"},
          ] as { key: Tab; label: string; icon: string }[]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              type="button"
              style={{
                padding: "14px 18px", background: "none", border: "none", borderBottom: tab === t.key ? "2.5px solid #ec4899" : "2.5px solid transparent",
                color: tab === t.key ? "#ec4899" : "#64748b", fontWeight: tab === t.key ? 700 : 500, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, marginBottom: -2,
              }}
            >
              <i className={`bi ${t.icon}`} />
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 20 }}>

          {/* ── MEMBERS TAB ─────────────────────────────────────────────── */}
          {tab === "members" && (
            <>
              {/* Filters */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <div style={{ position: "relative", flex: "1 1 220px" }}>
                  <i className="bi bi-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search customer…"
                    style={{ width: "100%", padding: "8px 12px 8px 34px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, color: "#1e293b", outline: "none", boxSizing: "border-box" }}
                  />
                </div>
                <select value={filterTier} onChange={e => setFilterTier(e.target.value as TierName | "All")}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, color: "#1e293b", background: "#fff" }}>
                  <option value="All">All Tiers</option>
                  {TIERS.map(t => <option key={t.name}>{t.name}</option>)}
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as LoyaltyMember["status"] | "All")}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, color: "#1e293b", background: "#fff" }}>
                  <option value="All">All Statuses</option>
                  <option>Active</option><option>Inactive</option><option>Suspended</option>
                </select>
              </div>
              <DataTable columns={memberColumns} rows={filteredMembers} rowKey={(r) => r.id} />
            </>
          )}

          {/* ── EARNING RULES TAB ───────────────────────────────────────── */}
          {tab === "rules" && (
            <div>
              <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 13, color: "#64748b" }}>
                Configure how customers earn loyalty points for various actions.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {rules.map(rule => (
                  <div key={rule.id} style={{ display: "flex", alignItems: "center", gap: 14, background: "#f8fafc", borderRadius: 12, padding: "14px 18px", border: "1.5px solid #e2e8f0" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{rule.ruleName}</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{rule.event}</div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: "#f59e0b", minWidth: 80, textAlign: "right" }}>
                      +{rule.pointsAwarded} pts
                    </div>
                    {/* Toggle switch */}
                    <button
                      type="button"
                      onClick={() => toggleRule(rule.id)}
                      style={{
                        width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                        background: rule.isActive ? "#ec4899" : "#cbd5e1",
                        position: "relative", flexShrink: 0, transition: "background .2s",
                      }}
                    >
                      <span style={{
                        position: "absolute", top: 3, left: rule.isActive ? 22 : 3, width: 18, height: 18,
                        borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)"
                      }} />
                    </button>
                    <button
                      onClick={() => { setEditRule({ ...rule }); setRuleModal(true); }}
                      style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── REDEMPTIONS TAB ─────────────────────────────────────────── */}
          {tab === "redemptions" && (
            <>
              <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 13, color: "#64748b" }}>
                Track and approve loyalty points redemptions on orders.
              </div>
              <DataTable columns={redemptionColumns} rows={redemptions} rowKey={(r) => r.id} />
            </>
          )}
        </div>
      </div>

      {/* ── Adjust Points Modal ───────────────────────────────────────────── */}
      {adjustModal && selectedMember && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 18, padding: 28, width: 420, maxWidth: "95vw", boxShadow: "0 24px 64px rgba(0,0,0,.2)" }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1e293b", marginBottom: 4 }}>Adjust Points</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>{selectedMember.customerName} — Current: <b>{fmt(selectedMember.points)} pts</b></div>
            {/* Add / Deduct toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {(["add", "deduct"] as const).map(t => (
                <button key={t} type="button" onClick={() => setAdjustType(t)}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1.5px solid", borderColor: adjustType === t ? "#ec4899" : "#e2e8f0", background: adjustType === t ? "#fff0f6" : "#fff", color: adjustType === t ? "#ec4899" : "#475569", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  {t === "add" ? "➕ Add Points" : "➖ Deduct Points"}
                </button>
              ))}
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Points to {adjustType}</label>
            <input
              type="number" min={1} value={adjustPoints} onChange={e => setAdjustPoints(e.target.value)}
              placeholder="e.g. 500"
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginTop: 4, marginBottom: 12, boxSizing: "border-box", outline: "none" }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Reason</label>
            <input
              value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
              placeholder="e.g. Goodwill gesture, correction…"
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginTop: 4, marginBottom: 20, boxSizing: "border-box", outline: "none" }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setAdjustModal(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={applyAdjust} disabled={!adjustPoints || !adjustReason}
                style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: !adjustPoints || !adjustReason ? "#f1f5f9" : "#ec4899", color: !adjustPoints || !adjustReason ? "#94a3b8" : "#fff", fontWeight: 700, cursor: !adjustPoints || !adjustReason ? "not-allowed" : "pointer" }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Rule Modal ───────────────────────────────────────────────── */}
      {ruleModal && editRule && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 18, padding: 28, width: 400, maxWidth: "95vw", boxShadow: "0 24px 64px rgba(0,0,0,.2)" }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1e293b", marginBottom: 20 }}>Edit Earning Rule</div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Rule Name</label>
            <input value={editRule.ruleName} onChange={e => setEditRule({ ...editRule, ruleName: e.target.value })}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginTop: 4, marginBottom: 12, boxSizing: "border-box", outline: "none" }} />
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Points Awarded</label>
            <input type="number" min={1} value={editRule.pointsAwarded} onChange={e => setEditRule({ ...editRule, pointsAwarded: parseInt(e.target.value) || 0 })}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginTop: 4, marginBottom: 20, boxSizing: "border-box", outline: "none" }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setRuleModal(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={saveRule} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#ec4899", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Tiers Modal ──────────────────────────────────────────────── */}
      {tiersModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 18, padding: 28, width: 480, maxWidth: "95vw", boxShadow: "0 24px 64px rgba(0,0,0,.2)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1e293b", marginBottom: 6 }}>Edit Tier Configuration</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 20 }}>Adjust thresholds, discounts, and multipliers per tier.</div>
            {editTiers.map((t, i) => (
              <div key={t.name} style={{ borderRadius: 12, border: `1.5px solid ${t.color}30`, background: t.bg, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: t.color, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <i className={`bi ${t.icon}`} />{t.name}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {[
                    { label: "Min Points", field: "minPoints" as keyof Tier },
                    { label: "Discount %",  field: "discount"  as keyof Tier },
                    { label: "Earn ×",      field: "earnMultiplier" as keyof Tier },
                  ].map(f => (
                    <div key={f.field}>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>{f.label}</div>
                      <input
                        type="number"
                        value={t[f.field] as number}
                        onChange={e => setEditTiers(prev => prev.map((x, j) => j === i ? { ...x, [f.field]: parseFloat(e.target.value) || 0 } : x))}
                        style={{ width: "100%", padding: "6px 10px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 13, boxSizing: "border-box", outline: "none" }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button onClick={() => setTiersModal(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => setTiersModal(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#ec4899", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Save Tiers</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
